import { Fragment, type ReactElement, type ReactNode } from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import type { IconName } from "../../components/ui/icon-registry";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { Text, textVariants } from "../../components/ui/text";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";
import { useElementSize } from "../../hooks/use-element-size";
import type { Color } from "../../lib/colors";
import { ColorSwatch } from "../color-swatch/color-swatch";
import { useWidgetBodyLoadingWhile } from "./contexts";
import { WidgetInteractive } from "./widget-interactive";
import { measureTextWidth } from "../../lib/measure-text";

/**
 * Figure size. The band is one type ramp at three scales — a compact card, a
 * full one, and a maximized preview — never a free size, so two bands on the
 * same page cannot land a half-step apart.
 */
const FIGURE_SIZE = { sm: "2xl", md: "3xl", lg: "4xl" } as const;
export type StatBandSize = keyof typeof FIGURE_SIZE;

/** The next rung down the ramp. `sm` is the floor — see {@link fittingSize}. */
const SMALLER_FIGURE: Record<StatBandSize, StatBandSize> = { lg: "md", md: "sm", sm: "sm" };

/**
 * Each rung's type size in px, and the widest character advance in a figure as
 * a share of it. The figures are `tabular`, so every digit is one fixed
 * advance and a character count IS a width; a separator (",", ".", "%") is
 * narrower than a digit, so the estimate runs high and the fit check errs
 * toward the smaller rung. It restates the theme's scale because the answer
 * picks a size class — it is needed before the type it would measure exists.
 */
const FIGURE_PX: Record<StatBandSize, number> = { sm: 24, md: 30, lg: 36 };
const FIGURE_CHAR_EM = 0.62;

/**
 * The inset each column wears, and the amount the band bleeds back over its
 * host's own padding so the first label lands on the host's text edge rather
 * than one inset in from it. The hover wash keeps a ring to breathe in and the
 * label still lines up under the card title.
 *
 * It is the SMALLER step on purpose. A widget card insets its body 16px (12px
 * compact), so the wash's outer edge stops short of the card border by the
 * difference: at `sm` that is 8px (4px compact), at `base` it would be 4px
 * (flush compact), and a selected column then reads as a tile jammed against
 * the frame rather than one resting inside it. `WidgetDisclosureRow` bleeds and
 * pads at the same step, so the two washes a card can hold sit on one edge.
 */
const COLUMN_INSET = "sm" as const;

/** {@link COLUMN_INSET} in px, for the width arithmetic below. */
const COLUMN_INSET_WIDTH = 8;

/**
 * The gap between a column and the rule beside it. The two constants below
 * spell out its pixel value because the fold threshold is arithmetic over it.
 */
const COLUMN_GAP = "sm" as const;

/**
 * The narrowest column any band gets, in px, its inset included: a two-digit
 * figure with a short caption on its baseline, or a one-word label with its
 * dot. Below this the band folds — see {@link StatBand}. A band with longer
 * labels needs more, and asks for it through {@link minColumnWidth}.
 */
const MIN_COLUMN_FLOOR = 88;

/** The label's type size in px; its width is measured, not estimated. */
const LABEL_PX = 12;

/** What a column's leading dot spends before the label: the swatch and its gap. */
const LABEL_DOT_WIDTH = 12;

/**
 * The narrowest column THIS band's columns fit in, in px, inset included:
 * the widest label with its dot, or the floor. "Updates this week" asks for
 * ~128px; "Positive" asks for the floor. A fixed threshold sized for the long
 * label folded a three-tile band of short ones into two rows at half width,
 * where one row of three had room to spare.
 */
function minColumnWidth(columns: readonly StatBandColumn[]): number {
  const widest = columns.reduce(
    (px, column) =>
      Math.max(
        px,
        measureTextWidth(column.label, { sizePx: LABEL_PX }) +
          (column.color ? LABEL_DOT_WIDTH : 0),
      ),
    0,
  );
  return Math.max(MIN_COLUMN_FLOOR, Math.ceil(widest) + 2 * COLUMN_INSET_WIDTH);
}

/**
 * The narrowest column a CONNECTED band will print as a column, in px, inset
 * included. A step column has no caption of its own, so it needs less than a
 * plain one: 56px of type holds a three-digit figure and its read at the `sm`
 * rung and a label of seven or eight letters — "Pending", "Renter", "Done"
 * whole, a longer name cut where a reader still knows the step. At 44px the
 * label was four letters and a mark, which names nothing. Below this the band
 * stops trying to be a row at all and stacks — see {@link connectedLayout}.
 */
const MIN_CONNECTED_COLUMN_WIDTH = 72;

/**
 * What one boundary between two columns spends, in px: the hairline plus the
 * `sm` (8px) gap either side of it.
 */
const BOUNDARY_WIDTH = 17;

/**
 * Columns per row once the band has folded. A fixed two rather than "as many
 * as fit": a four-column band becomes a 2×2 instead of a 3+1 with an orphan,
 * and a two-column band never folds at all because two per row is already its
 * shape. A lone last column in an odd count takes its row's full width.
 */
const FOLDED_PER_ROW = 2;

/**
 * The band's floor, in px — two lines of type plus their inset, and the height
 * that fills the two rows a 48px grid leaves a card body after its 36px header
 * and insets. A card whose band hugs its type instead hands the difference to
 * the grid's whole-row rounding, and that slack has to be parked somewhere:
 * above the figures it reads as a hole under the title, below them as a card
 * that forgot to end. A host on a different pitch overrides it. A folded band
 * is this tall PER ROW.
 */
export const STAT_BAND_MIN_HEIGHT = 60;

/**
 * Width of a connector cell, in px. Fixed, so the columns either side of it
 * keep the same share of the band whatever the figure says; wide enough for a
 * four-digit rate ("101.4%") at the connector's type size.
 */
const CONNECTOR_WIDTH = 64;

/**
 * What one CONNECTED boundary spends, in px: the connector cell plus the same
 * `sm` gap either side that {@link BOUNDARY_WIDTH} pays for its hairline.
 */
const CONNECTOR_BOUNDARY_WIDTH = CONNECTOR_WIDTH + 2 * COLUMN_INSET_WIDTH;

/**
 * The three shapes a connected band takes, widest first. The connector cells
 * are fixed and the columns flex, so a band that loses width loses it from
 * the columns alone: six connectors on a half-width card spend more than the
 * card has, and seven steps split what is left at three pixels each — a row
 * of clipped digit edges. The band measures itself and steps down instead:
 *
 * - `wide`: a connector cell on every boundary, the read printed on it.
 * - `compact`: the boundary is a hairline again and the read moves into the
 *   column it leaves, as a caption under the figure ("→ 57.1%"). The columns
 *   get the connectors' width back and the sequence is still one row.
 * - `stacked`: the steps run top to bottom, one line each, and the read sits
 *   between them with a down arrow. The funnel everyone knows. Height is the
 *   axis a workflow with many steps can have; width is the one it cannot.
 */
type ConnectedLayout = "wide" | "compact" | "stacked";

function connectedLayout(width: number, count: number): ConnectedLayout {
  // Unmeasured: the row it is on every board wide enough to hold one.
  if (width === 0 || count <= 1) return "wide";
  const boundaries = count - 1;
  const wideColumn = (width - boundaries * CONNECTOR_BOUNDARY_WIDTH) / count;
  if (wideColumn >= MIN_CONNECTED_COLUMN_WIDTH) return "wide";
  const compactColumn = (width - boundaries * BOUNDARY_WIDTH) / count;
  if (compactColumn >= MIN_CONNECTED_COLUMN_WIDTH) return "compact";
  return "stacked";
}

/**
 * The largest rung at or below `size` whose widest figure still fits a column
 * this wide.
 *
 * The band folds before it truncates, but a CONNECTED band may not fold — the
 * sequence is the point — so stepping down the ramp is the move it has
 * instead. A truncated figure is the one cut the band cannot take: a label
 * reads short ("Inspection…") and a caption wraps under its figure, but
 * "1,5…" is not a number. WIDTH is what runs out, and the column count and the
 * digits in it are both set by the data, so the rung is answered here rather
 * than by a caller reading its own height — a taller card holds the same
 * columns and the same digits as a short one.
 */
function fittingSize(
  size: StatBandSize,
  columns: readonly StatBandColumn[],
  columnWidth: number,
): StatBandSize {
  // Unmeasured (or narrower than its own boundaries): the band keeps the rung
  // it was handed rather than flashing the floor on its first paint.
  if (columnWidth <= 0) return size;
  const widest = columns.reduce((chars, column) => Math.max(chars, column.value.length), 0);
  const room = columnWidth - 2 * COLUMN_INSET_WIDTH;
  let fitted = size;
  while (fitted !== "sm" && widest * FIGURE_CHAR_EM * FIGURE_PX[fitted] > room) {
    fitted = SMALLER_FIGURE[fitted];
  }
  return fitted;
}

export type StatBandColumn = {
  /** Stable key for the column. */
  id: string;
  /** Names the column. Muted, like the caption — see the ink note on
   *  {@link StatBand}. */
  label: string;
  /** The figure, preformatted: the caller owns number formatting and locale. */
  value: string;
  /** A fact about the figure, set on its baseline. */
  caption?: string | undefined;
  /** A leading dot before the label, for a column that names a palette member. */
  color?: Color | undefined;
  /** Quiet figure — a column that counted nothing, or one that is inactive. */
  muted?: boolean | undefined;
  /**
   * Whether this column's panel is the open one. Omit entirely for a band with
   * no selection model: the column then announces no expanded state at all
   * rather than a permanent `false`.
   */
  selected?: boolean | undefined;
  /** Omit for a column that discloses nothing: it renders as text, not a
   *  button, and a press on it falls through to whatever owns the surface. */
  onSelect?: (() => void) | undefined;
  /** Button name where the visible label is not the whole story. */
  ariaLabel?: string | undefined;
};

/** Ink for a connector's figure. `muted` reads as a measure; a state tone
 *  reads as a rate that should catch the eye. */
export type StatBandConnectorTone = "default" | "muted" | "success" | "warning" | "destructive";

/**
 * What one boundary between two columns says about the STEP from the left
 * column to the right one. A band with connectors is a sequence: the columns
 * are the steps in the order given, and each connector is the read across a
 * boundary — a rate, a ratio, a time between the two.
 */
export type StatBandConnector = {
  /** The step's figure, preformatted: the caller owns number formatting. */
  value: string;
  tone?: StatBandConnectorTone | undefined;
  /** A second fact about the step, under the figure. */
  caption?: string | undefined;
  /** A glyph before the caption, for a caption that is a measure of a kind
   *  the glyph names (a clock before a duration). */
  captionIcon?: IconName | undefined;
  /** What the figures measure, on hover. A derived number is not self-explaining. */
  tooltip?: ReactNode;
  /** The boundary read as a sentence, for assistive tech: "Inspections to
   *  Pre-Contracts: 101.4%, 14 days". The cell is a plain group without it. */
  ariaLabel?: string | undefined;
};

export type StatBandProps = {
  columns: readonly StatBandColumn[];
  /**
   * One connector per boundary: `connectors[i]` sits between `columns[i]` and
   * `columns[i + 1]`, in place of that boundary's hairline. A boundary with no
   * entry keeps its rule. A connected band is a SEQUENCE, so it never folds
   * into rows of two: that would put a step's arrow at the end of one row and
   * its target at the start of the next, and the read across the boundary is
   * the whole point. Short of width it steps down a ramp of its own instead —
   * the read moves into the column, then the steps stack top to bottom (see
   * {@link StatBand}).
   */
  connectors?: readonly StatBandConnector[] | undefined;
  /** Figure scale — a ceiling, not a setting: the band steps it down where a
   *  column is too narrow to print its widest figure whole. */
  size?: StatBandSize | undefined;
  /** Floor for each row of the band, in px. Defaults to
   *  {@link STAT_BAND_MIN_HEIGHT}; a floor rather than a fixed height leaves the
   *  narrow column that wraps its caption room to grow. */
  minHeight?: number | undefined;
  /**
   * The figures are not in yet. Every column keeps its label, its rule, its
   * wash and its press target, and stands a placeholder where the figure and
   * caption go — sized in the figure's own type unit, so the label sits
   * exactly where it will sit once the number lands. A first paint and the
   * re-query after the window or scope changes are then one picture: the band
   * never leaves the page to make room for a loading state, so nothing above
   * or below it moves, and a panel open beneath a column stays open. The
   * placeholders are delayed the way every first-paint gate in the library is,
   * so an answer that lands inside the window never shows one. The band reports
   * itself as a loading body to the measuring card while this is set.
   */
  loading?: boolean | undefined;
  /**
   * Fill the height the host hands the band. Each row takes an equal share of
   * the slack and every column centers its type in the taller row, so a card
   * whose body is rounded up to whole grid rows parks the remainder INSIDE
   * the figures rather than as a blank strip under whatever sits below the
   * band. The band's natural height is unchanged — a host measuring content
   * (an autoHeight card) reads the same number either way.
   */
  grow?: boolean | undefined;
};

/**
 * `StatBand` — a row of headline figures, each one label over figure, a
 * hairline rule on every boundary, and every column an optional press target
 * for the panel it discloses beneath the band. With `connectors` the band is a
 * SEQUENCE: each boundary carries the read across it (a rate, a time) in place
 * of its rule, and the band keeps the steps in order.
 *
 * Four decisions make it one shape rather than a per-caller arrangement:
 *
 * - **Rules, not boxes.** A bordered tile inside an already-bordered card is
 *   double framing, and each box charges its own inset against the type. The
 *   rule costs a pixel, scales to any column count, and reads the columns as
 *   one table rather than a shelf of cards. The wash is what marks the press
 *   target; a permanent border is not needed to promise one.
 * - **One ink on the figure.** The figure carries full ink because it is the
 *   point; the label and caption are both muted because they annotate it — one
 *   names the column, the other states a fact about the number. The label
 *   keeps its weight so it still reads as the column's heading, and the two
 *   quiet lines are told apart by size and position rather than by a third
 *   grey: the label sits above the figure, the caption beside it. Full ink on
 *   the label would match the card's own title, which is the same ink one step
 *   larger, and a headed card then reads as two rows of headings over one row
 *   of numbers.
 * - **The caption rides the figure's baseline.** It is a fact ABOUT the number;
 *   parked on a third line it groups with neither neighbour and spends a whole
 *   row of a quantized grid on 14px of type. It wraps under the figure only
 *   where the column is too narrow to hold both.
 * - **The band folds before it truncates.** Once its width cannot give every
 *   column {@link minColumnWidth} — sized from its own labels — the row
 *   breaks into rows of two, and a
 *   horizontal rule divides the rows the way the vertical ones divide the
 *   columns — inset to the type on both ends, so every rule in the band spans
 *   exactly what sits beside it. Four figures at 85px each are four truncated
 *   labels over four captions pushed under their figures, which is every line
 *   the band has cut short; the same four two-up have room for all of it and
 *   cost the card the rows a taller body costs. The band measures ITSELF, not
 *   the viewport and not a grid span: a card's width is set by its board and
 *   the board's by whatever pane holds it, so a half-width card on a wide
 *   board and a full-width card in a split window are one problem with one
 *   answer, and a caller branching on its own size would get one of them
 *   wrong. The mount measurement lands in a layout effect and re-renders
 *   before the first paint, so the unfolded row is never shown to a band that
 *   needs to fold. A connected band cannot fold, so it has a ramp of its own
 *   ({@link connectedLayout}): the connector cells give their width back to
 *   the columns first, and the steps stack top to bottom when even that is
 *   not enough. On every rung the figure steps down the type ramp before it
 *   truncates — `size` is the ceiling a host asks for and the width decides
 *   what it gets (see {@link fittingSize}). Either way the figure is never the
 *   line that gets cut short.
 */
export function StatBand({
  columns,
  connectors,
  size = "md",
  minHeight = STAT_BAND_MIN_HEIGHT,
  loading = false,
  grow = false,
}: StatBandProps) {
  useWidgetBodyLoadingWhile(loading);
  const { ref, size: measured } = useElementSize<HTMLDivElement>();
  const connected = connectors !== undefined && connectors.length > 0;
  const layout: ConnectedLayout = connected
    ? connectedLayout(measured.width, columns.length)
    : "wide";

  if (connected && layout === "stacked") {
    return (
      <Box
        ref={ref}
        direction="column"
        gap="2xs"
        marginXNegative={COLUMN_INSET}
        shrink={false}
        aria-busy={loading || undefined}
      >
        {columns.map((column, index) => {
          const connector = index > 0 ? connectors[index - 1] : undefined;
          return (
            <Fragment key={column.id}>
              {connector ? (
                <StatBandStackedConnector connector={connector} loading={loading} />
              ) : index > 0 ? (
                <Box paddingX={COLUMN_INSET} shrink={false}>
                  <Separator />
                </Box>
              ) : null}
              <StatBandStackedCell column={column} loading={loading} />
            </Fragment>
          );
        })}
      </Box>
    );
  }

  // Zero is "not measured yet", and an unmeasured band lays out as the row it
  // is on every board wide enough to hold one. A connected band is a sequence
  // and lays out as the row on both of its row rungs.
  const fits =
    connected ||
    measured.width === 0 ||
    measured.width >=
      columns.length * minColumnWidth(columns) + (columns.length - 1) * BOUNDARY_WIDTH;
  // At least one per row: an empty band must not loop, and a folded band of
  // one column is that column.
  const perRow = Math.max(1, fits ? columns.length : FOLDED_PER_ROW);
  // What one column of a FULL row gets, once the boundaries between them are
  // paid for, and the rung whose figures fit it. A short last row is wider per
  // column, so a figure that fits here fits everywhere — and the band wears
  // one rung throughout either way. A compact connected band pays a hairline
  // per boundary, not a connector cell.
  const boundaryWidth =
    connected && layout === "wide" ? CONNECTOR_BOUNDARY_WIDTH : BOUNDARY_WIDTH;
  const columnWidth = (measured.width - (perRow - 1) * boundaryWidth) / perRow;
  const figureSize = fittingSize(size, columns, columnWidth);
  const rows: StatBandColumn[][] = [];
  for (let start = 0; start < columns.length; start += perRow) {
    rows.push(columns.slice(start, start + perRow));
  }

  return (
    <Box
      ref={ref}
      direction="column"
      gap={COLUMN_GAP}
      marginXNegative={COLUMN_INSET}
      shrink={false}
      grow={grow}
      aria-busy={loading || undefined}
    >
      {rows.map((row, rowIndex) => (
        // Rows are positional: a column's identity keys its cell, and the row
        // it lands in is a function of the band's width, not of the column.
        <Fragment key={rowIndex}>
          {/* One rule per row boundary, inset by the column inset on both ends
              so it runs from the first column's text edge to the last one's —
              the horizontal twin of the vertical rule's `marginY`. */}
          {rowIndex > 0 ? (
            <Box paddingX={COLUMN_INSET} shrink={false}>
              <Separator />
            </Box>
          ) : null}
          <Box direction="row" align="stretch" gap={COLUMN_GAP} grow={grow}>
            {row.map((column, index) => {
              // A connected band is one row, so the boundary before column
              // `index` of this row is boundary `index - 1` of the band.
              const connector = index > 0 ? connectors?.[index - 1] : undefined;
              // Compact: the read of the step LEAVING this column prints in
              // the column, so the boundary after it is the one it carries.
              const read = layout === "compact" ? connectors?.[index] : undefined;
              return (
              <Fragment key={column.id}>
                {/* One hairline per boundary. Equal blocks floating in shared
                    whitespace read as one wide smear with no landing point; the
                    rule is what makes each figure a column of its own. The
                    wrapper stretches and then insets itself by the column's own
                    inset, so the rule spans exactly the type beside it — run it
                    the row's full height and its ends hang past the text into
                    the air, drawing the gap rather than the column. It is a ROW
                    so the rule stretches on its cross axis; in a column it
                    would stretch sideways and collapse to nothing tall. A
                    connector takes the boundary's place: it IS the rule, with
                    the step's read printed on it. */}
                {connector && layout === "wide" ? (
                  <StatBandConnectorCell connector={connector} loading={loading} />
                ) : index > 0 ? (
                  <Box direction="row" alignSelf="stretch" marginY="xs" shrink={false}>
                    <Separator orientation="vertical" />
                  </Box>
                ) : null}
                <StatBandCell
                  column={column}
                  read={read}
                  compact={layout === "compact"}
                  size={figureSize}
                  minHeight={minHeight}
                  loading={loading}
                />
              </Fragment>
              );
            })}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

/**
 * The placeholder that stands where a column's figure and caption go. It is
 * sized in the FIGURE's type unit rather than in px: the box wears the figure's
 * own size class and is `1em` tall — the line box a `leading="none"` figure
 * occupies — so the label above it lands on the same pixel loading and loaded,
 * and a change to the type ramp moves both together. The two bars are the
 * figure (a few digits wide) and the caption beside it, and both are delayed
 * so a fast answer paints numbers with no shimmer in between.
 */
function StatBandFigurePlaceholder({ size }: { size: StatBandSize }) {
  return (
    <Box
      fill="width"
      direction="row"
      align="center"
      gap="sm"
      className={textVariants({ size: FIGURE_SIZE[size], leading: "none" })}
      height="1em"
    >
      <Skeleton delayed height="0.7em" width="2.5em" />
      <Skeleton delayed height={8} width={72} />
    </Box>
  );
}

const CONNECTOR_TONE_VARIANT: Record<
  StatBandConnectorTone,
  "muted" | "destructive" | "warning" | "success" | undefined
> = {
  default: undefined,
  muted: "muted",
  destructive: "destructive",
  warning: "warning",
  success: "success",
};

/** Wraps a connector's cell in its tooltip, where it has one and the figure is
 *  in. A loading cell explains nothing yet. */
function withConnectorTooltip(
  connector: StatBandConnector,
  loading: boolean,
  cell: ReactElement,
) {
  if (!connector.tooltip || loading) return cell;
  return (
    <Tooltip>
      <TooltipTrigger render={cell} />
      <TooltipPopup side="top">{connector.tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/** The connector's caption: its glyph, where it has one, then the text. */
function ConnectorCaption({ connector }: { connector: StatBandConnector }) {
  if (!connector.caption) return null;
  return (
    <Box direction="row" align="center" gap="2xs" minWidth={0}>
      {connector.captionIcon ? (
        <Icon name={connector.captionIcon} size="2xs" color="faint" aria-hidden />
      ) : null}
      <Text size="2xs" variant="muted" tabular truncate>
        {connector.caption}
      </Text>
    </Box>
  );
}

/**
 * The boundary read: an arrow for the direction of the sequence, the step's
 * figure under it, and the caption under that. The arrow is quiet — it is
 * grammar, not data — and the figure carries the ink the caller chose. While
 * the band loads the cell stands two bars where the figure and caption go, so
 * the columns either side keep their share and nothing moves when the rate
 * lands.
 */
function StatBandConnectorCell({
  connector,
  loading,
}: {
  connector: StatBandConnector;
  loading: boolean;
}) {
  const cell = (
    <Box
      data-slot="stat-band-connector"
      direction="column"
      align="center"
      justify="center"
      gap="2xs"
      width={CONNECTOR_WIDTH}
      paddingY="xs"
      shrink={false}
      role={connector.ariaLabel ? "group" : undefined}
      aria-label={connector.ariaLabel}
    >
      <Icon name="arrow-right" size="sm" color="faint" aria-hidden />
      {loading ? (
        <>
          <Skeleton delayed height={10} width={40} />
          {connector.caption ? <Skeleton delayed height={8} width={28} /> : null}
        </>
      ) : (
        <>
          <Text
            size="xs"
            weight="semibold"
            variant={CONNECTOR_TONE_VARIANT[connector.tone ?? "default"]}
            tabular
            truncate
          >
            {connector.value}
          </Text>
          <ConnectorCaption connector={connector} />
        </>
      )}
    </Box>
  );
  return withConnectorTooltip(connector, loading, cell);
}

/**
 * The compact rung's read, on the caption line of the column the step leaves:
 * the arrow, the figure, the caption in one quiet row. The arrow stays because
 * it is what says "this number is about the step out, not about the count
 * above it".
 */
function StatBandConnectorRead({
  connector,
  loading,
}: {
  connector: StatBandConnector;
  loading: boolean;
}) {
  const read = (
    <Box
      data-slot="stat-band-connector"
      direction="row"
      align="center"
      gap="2xs"
      minWidth={0}
      role={connector.ariaLabel ? "group" : undefined}
      aria-label={connector.ariaLabel}
    >
      <Icon name="arrow-right" size="2xs" color="faint" aria-hidden />
      {loading ? (
        <Skeleton delayed height={8} width={32} />
      ) : (
        <>
          <Text
            size="2xs"
            weight="semibold"
            variant={CONNECTOR_TONE_VARIANT[connector.tone ?? "default"]}
            tabular
            truncate
          >
            {connector.value}
          </Text>
          <ConnectorCaption connector={connector} />
        </>
      )}
    </Box>
  );
  return withConnectorTooltip(connector, loading, read);
}

/**
 * The stacked rung's read, on its own line between two steps: a down arrow
 * for the direction the sequence now runs, then the figure and its caption.
 * Inset to the column text edge, so it reads as the seam between the two rows
 * it sits between rather than as a row of its own.
 */
function StatBandStackedConnector({
  connector,
  loading,
}: {
  connector: StatBandConnector;
  loading: boolean;
}) {
  const row = (
    <Box
      data-slot="stat-band-connector"
      direction="row"
      align="center"
      gap="xs"
      paddingX={COLUMN_INSET}
      minWidth={0}
      role={connector.ariaLabel ? "group" : undefined}
      aria-label={connector.ariaLabel}
    >
      <Icon name="arrow-down" size="2xs" color="faint" aria-hidden />
      {loading ? (
        <Skeleton delayed height={8} width={40} />
      ) : (
        <>
          <Text
            size="xs"
            weight="semibold"
            variant={CONNECTOR_TONE_VARIANT[connector.tone ?? "default"]}
            tabular
            truncate
          >
            {connector.value}
          </Text>
          <ConnectorCaption connector={connector} />
        </>
      )}
    </Box>
  );
  return withConnectorTooltip(connector, loading, row);
}

/** The layout props every column's shell carries, on whichever rung. */
type ColumnShell = Pick<
  BoxProps,
  "flex" | "fill" | "minWidth" | "minHeight" | "rounded" | "justify" | "paddingX" | "paddingY"
>;

/** The press shell every column shares, whichever rung it is on. */
function columnShell(column: StatBandColumn, body: ReactNode, shell: ColumnShell) {
  return column.onSelect != null ? (
    <WidgetInteractive
      as="button"
      type="button"
      {...shell}
      cursor="pointer"
      hoverBackground
      background={column.selected ? "selected" : undefined}
      aria-expanded={column.selected}
      aria-label={column.ariaLabel}
      onClick={column.onSelect}
    >
      {body}
    </WidgetInteractive>
  ) : (
    <Box {...shell}>{body}</Box>
  );
}

function StatBandCell({
  column,
  read,
  compact,
  size,
  minHeight,
  loading,
}: {
  column: StatBandColumn;
  /** The compact rung's boundary read, for the step out of this column. */
  read: StatBandConnector | undefined;
  /** Whether the band is on the compact rung (some column carries a read). */
  compact: boolean;
  size: StatBandSize;
  minHeight: number;
  loading: boolean;
}) {
  const body = (
    <Box fill="width" align="start" gap="xs">
      <Box direction="row" align="center" gap="xs" fill="width" minWidth={0}>
        {column.color ? (
          <ColorSwatch color={column.color} size="xs" rounded="full" />
        ) : null}
        <Text size="xs" weight="medium" variant="muted" truncate>
          {column.label}
        </Text>
      </Box>
      {loading ? (
        <StatBandFigurePlaceholder size={size} />
      ) : (
        <Box fill="width" direction="row" align="baseline" wrap gapX="sm" gapY="2xs">
          <Text
            size={FIGURE_SIZE[size]}
            weight="bold"
            variant={column.muted ? "muted" : undefined}
            tabular
            truncate
            // A headline number carries its own optical padding, so the type
            // scale's leading would open a gap under the label that no spacing
            // token asked for. Pinned to glyph height, the gap is the one the
            // stack sets.
            leading="none"
          >
            {column.value}
          </Text>
          {column.caption ? (
            <Text size="2xs" variant="muted" tabular truncate>
              {column.caption}
            </Text>
          ) : null}
        </Box>
      )}
      {/* The read stays while loading: its arrow and placeholder hold the
          line so the column is one height loading and loaded. */}
      {read ? <StatBandConnectorRead connector={read} loading={loading} /> : null}
    </Box>
  );
  const shell = {
    flex: 1,
    minWidth: 0,
    minHeight,
    rounded: "lg",
    // Centered, for the column with no caption to run to the row's floor and
    // for the wrapped narrow one whose siblings stretch past it. On the
    // compact rung the columns start at the top instead: the last step has
    // no read under its figure, and centered it would drop its label below
    // the others' — the labels are the line a row is read along.
    justify: compact ? "start" : "center",
    paddingX: COLUMN_INSET,
    paddingY: "xs",
  } satisfies ColumnShell;
  return columnShell(column, body, shell);
}

/**
 * One step of a stacked band: the swatch and label on the left, the figure on
 * the right, on one line. The figure keeps the ink and the weight it has in a
 * column, one rung down the type ramp — a row is read across, not looked at,
 * and a headline size on every line would make eight headlines.
 */
function StatBandStackedCell({
  column,
  loading,
}: {
  column: StatBandColumn;
  loading: boolean;
}) {
  const body = (
    <Box direction="row" align="center" gap="sm" fill="width" minWidth={0}>
      <Box direction="row" align="center" gap="xs" grow minWidth={0}>
        {column.color ? (
          <ColorSwatch color={column.color} size="xs" rounded="full" />
        ) : null}
        <Text size="xs" weight="medium" variant="muted" truncate>
          {column.label}
        </Text>
      </Box>
      {loading ? (
        <Skeleton delayed height={12} width={28} />
      ) : (
        <Box direction="row" align="baseline" gap="xs" shrink={false}>
          <Text
            size="base"
            weight="bold"
            variant={column.muted ? "muted" : undefined}
            tabular
            leading="none"
          >
            {column.value}
          </Text>
          {column.caption ? (
            <Text size="2xs" variant="muted" tabular truncate>
              {column.caption}
            </Text>
          ) : null}
        </Box>
      )}
    </Box>
  );
  const shell = {
    fill: "width",
    minWidth: 0,
    rounded: "lg",
    paddingX: COLUMN_INSET,
    paddingY: "xs",
  } satisfies ColumnShell;
  return columnShell(column, body, shell);
}
